import { useEffect, useState } from 'react';
import { CaseAssetItem } from '../../../../types';
import { DetailViewCard } from '../../../Compounds/CardWithTitle';
import { ComponentSpinner } from '../../../Compounds/Loading/ComponentSpinner';
import { RelatedRecordsColumn, RelatedRecordsTable } from '../../../Compounds/RelatedRecordsTable/RelatedRecordsTable';
import { fetchCaseAssets } from '../../../../Api/Cases/fetchCaseAssets';
import { InventoryRecordDrawer } from '../../../Compounds/InventoryRecordDrawer/InventoryRecordDrawer';

const usaCurrency = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

const columns: RelatedRecordsColumn<CaseAssetItem>[] = [
  { header: 'Address', cellClass: 'break-words', text: (asset) => asset.address || '' },
  { header: 'Supplier', headerClass: 'w-32', cellClass: 'truncate', text: (asset) => asset.supplier?.name || '' },
  { header: 'Product', headerClass: 'w-32', cellClass: 'truncate', text: (asset) => asset.product?.name || '' },
  {
    header: 'Monthly Cost',
    headerClass: 'w-28',
    cellClass: 'whitespace-nowrap',
    text: (asset) => usaCurrency.format(asset.mRC || 0)
  }
];

export interface CaseInventoryTableProps {
  caseId: string;
}

export function CaseInventoryTable({ caseId }: CaseInventoryTableProps) {
  const [assets, setAssets] = useState<CaseAssetItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [collapsed, setCollapsed] = useState<boolean>(true);
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);

  useEffect(() => {
    if (!caseId) return;
    let active = true;
    setLoading(true);
    fetchCaseAssets(caseId)
      .then((res) => {
        if (active && res.data) setAssets(res.data);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [caseId]);

  return (
    <>
      <DetailViewCard
        collapsed={collapsed}
        enableCollapse
        onCollapseClick={setCollapsed}
        childrenWrapperClass="overflow-x-auto"
        title={`Inventory (${assets.length})`}>
        <>
          {loading && (
            <div className="mt-4">
              <ComponentSpinner />
            </div>
          )}
          {!loading && assets.length === 0 && <div className="text-center">No Inventory</div>}
          {!loading && assets.length > 0 && (
            <RelatedRecordsTable
              testId="case-inventory"
              records={assets}
              recordKey={(asset) => asset.id}
              columns={columns}
              openTitle="Open Inventory"
              onOpen={(asset) => setSelectedAssetId(asset.id)}
            />
          )}
        </>
      </DetailViewCard>
      {selectedAssetId && (
        <InventoryRecordDrawer
          assetId={selectedAssetId}
          onClose={() => setSelectedAssetId(null)}
          testIdPrefix="case-inventory"
        />
      )}
    </>
  );
}
